'use client';

import React, { useState } from 'react';
import { esOptimizable } from '@/lib/foto-optimizable';

/**
 * `<img>` del sitio público con tres mejoras y el mismo DOM de siempre:
 *
 *  1. **Variantes por ancho** (`srcSet` + `sizes`) servidas por el optimizador
 *     de Next (`/_next/image`), en AVIF/WebP y al tamaño que la pantalla
 *     necesita. Antes toda foto del builder bajaba el original: 862 KB para
 *     una foto que se ve a 427 px de ancho. Sólo para los hosts que
 *     `next.config.ts` habilita (Supabase, Unsplash) y nunca para SVG ni GIF,
 *     que el optimizador no transforma.
 *  2. **`loading="lazy"` por defecto** (se puede pedir `eager`): lo que está
 *     fuera de la pantalla no se descarga hasta que hace falta.
 *  3. **Respaldo**: si una variante falla (el optimizador no pudo con ese
 *     objeto), se vuelve al `src` original, que es exactamente lo que se
 *     servía antes. Si también falla, se avisa al `onError` del caller.
 *
 * Es un reemplazo 1:1 de `<img>`: acepta los mismos atributos y los pasa tal
 * cual, así que no cambia ni el layout ni los estilos.
 */

export type VarianteImg = 'foto' | 'icono';

/** Anchos que acepta el optimizador (deviceSizes ∪ imageSizes de Next). */
const ANCHOS: Record<VarianteImg, number[]> = {
  foto: [640, 750, 828, 1080, 1200, 1920],
  icono: [96, 128, 256, 384, 640],
};

const CALIDAD = 75;

const NO_OPTIMIZABLE = /\.(svg|gif)(\?|#|$)/i;

/** `srcSet` con variantes de `/_next/image`, o `undefined` si no corresponde. */
export function srcSetDe(src: string | undefined | null, variante: VarianteImg = 'foto'): string | undefined {
  if (!src || typeof src !== 'string') return undefined;
  if (src.startsWith('data:') || src.startsWith('blob:')) return undefined;
  if (NO_OPTIMIZABLE.test(src)) return undefined;
  if (!esOptimizable(src)) return undefined;
  const url = encodeURIComponent(src);
  return ANCHOS[variante].map((w) => `/_next/image?url=${url}&w=${w}&q=${CALIDAD} ${w}w`).join(', ');
}

/** `sizes` por defecto según la variante: la foto ocupa el ancho de la pantalla, el ícono ~200 px. */
const SIZES_DEFAULT: Record<VarianteImg, string> = {
  foto: '100vw',
  icono: '200px',
};

export interface ImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /** Qué tamaños generar. `foto` (por defecto) o `icono` (logos, avatares, insignias). */
  variante?: VarianteImg;
}

export const Img = React.forwardRef<HTMLImageElement, ImgProps>(function Img(
  { variante = 'foto', src, srcSet, sizes, loading, decoding, onError, ...resto },
  ref
) {
  // Tras un fallo de la variante se sirve el original: `null` = sin srcSet.
  const [sinVariantes, setSinVariantes] = useState(false);
  const generado = sinVariantes ? undefined : srcSet ?? srcSetDe(typeof src === 'string' ? src : undefined, variante);

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={src}
      srcSet={generado}
      sizes={generado ? sizes ?? SIZES_DEFAULT[variante] : sizes}
      loading={loading ?? 'lazy'}
      decoding={decoding ?? 'async'}
      onError={(e) => {
        if (generado) {
          // Reintento con el original, exactamente lo que se servía antes.
          setSinVariantes(true);
          return;
        }
        onError?.(e);
      }}
      {...resto}
    />
  );
});

export default Img;
